"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _componentsBase = require("@telus-uds/components-base");
var _systemConstants = require("@telus-uds/system-constants");
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _utils = require("../utils");
var _Segment = _interopRequireDefault(require("./Segment"));
var _useCountdown = _interopRequireDefault(require("./useCountdown"));
var _types = require("./types");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
// Reading these from the RN palette since they will be used to generate
// the `Typography` tokens

const DAYS = 'days';
const HOURS = 'hours';
const MINUTES = 'minutes';
const SECONDS = 'seconds';
const [selectProps, selectedSystemPropTypes] = (0, _componentsBase.selectSystemProps)([_utils.htmlAttrs]);
const Container = /*#__PURE__*/_styledComponents.default.div.withConfig({
  displayName: "Countdown__Container",
  componentId: "components-web__sc-18tqcb2-0"
})(_ref => {
  let {
    variant: {
      feature,
      inverse,
      large
    },
    themeTokens,
    gradient
  } = _ref;
  return {
    ...(large || feature ? {
      display: 'flex',
      flex: 0
    } : {}),
    ...(feature && {
      borderRadius: themeTokens.containerBorderRadius,
      borderColor: themeTokens.containerBorderColor,
      borderStyle: 'solid',
      borderWidth: `${themeTokens.containerBorderWidth}px`,
      justifyContent: 'center',
      paddingBottom: `${themeTokens.containerPaddingBottomTop}px`,
      paddingLeft: `${themeTokens.containerPaddingLeftRight}px`,
      paddingRight: `${themeTokens.containerPaddingLeftRight}px`,
      paddingTop: `${themeTokens.containerPaddingBottomTop}px`,
      width: 'fit-content'
    }),
    ...(feature && !inverse && gradient && {
      background: `linear-gradient(#fff 0 0) padding-box, ${gradient} border-box`
    })
  };
});
const getLabelTokens = themeTokens => ({
  color: themeTokens.labelBorderColor,
  fontSize: themeTokens.labelFontSize,
  fontWeight: themeTokens.labelFontWeight,
  lineHeight: themeTokens.labelLineHeight
});
const getMainTextTokens = _ref2 => {
  let {
    labelBorderColor,
    textTimerFontWeight,
    textFontSize,
    textLineHeight,
    textTimerFontName
  } = _ref2;
  return {
    color: labelBorderColor,
    lineHeight: textLineHeight,
    fontWeight: textTimerFontWeight,
    fontSize: textFontSize,
    fontName: textTimerFontName
  };
};
const Countdown = /*#__PURE__*/_react.default.forwardRef((_ref3, ref) => {
  let {
    copy = 'en',
    targetTime,
    tokens,
    variant = {},
    units = [DAYS, HOURS, MINUTES, SECONDS],
    ...rest
  } = _ref3;
  const [days, hours, minutes, seconds] = (0, _useCountdown.default)(targetTime);
  const viewport = (0, _componentsBase.useViewport)();
  const {
    feature,
    large,
    noDivider
  } = variant;
  const {
    themeOptions
  } = (0, _componentsBase.useTheme)();
  const {
    enableMediaQueryStyleSheet
  } = themeOptions;
  const useTokens = enableMediaQueryStyleSheet ? _componentsBase.useResponsiveThemeTokens : _componentsBase.useThemeTokens;
  const resolvedTokens = useTokens('Countdown', tokens, variant, !enableMediaQueryStyleSheet && {
    viewport
  });
  const themeTokens = enableMediaQueryStyleSheet ? resolvedTokens[viewport] : resolvedTokens;
  const segmentFontSize = themeTokens.textFontSize;
  const semanticGradient = themeTokens.containerGradient && (0, _utils.transformGradient)(themeTokens.containerGradient);
  const mainTextTokens = getMainTextTokens(themeTokens);
  const labelTokens = getLabelTokens(themeTokens);
  let labelStyles;
  let labelMediaIds;
  let mainTextStyles;
  let mainTextMediaIds;
  let containerStyles;
  let containerMediaIds;
  if (enableMediaQueryStyleSheet) {
    const {
      transformedLabelThemeTokens,
      transformedMainTextThemeTokens,
      transformedContainerThemeTokens
    } = Object.entries(themeTokens).reduce((acc, _ref4) => {
      let [vp, viewportTokens] = _ref4;
      acc.transformedLabelThemeTokens[vp] = {
        fontSize: viewportTokens.labelFontSize,
        lineHeight: viewportTokens.labelLineHeight
      };
      acc.transformedMainTextThemeTokens[vp] = {
        fontSize: viewportTokens.textFontSize,
        lineHeight: viewportTokens.textLineHeight
      };
      acc.transformedContainerThemeTokens[vp] = {
        containerPaddingBottomTop: viewportTokens.containerPaddingBottomTop,
        containerPaddingLeftRight: viewportTokens.containerPaddingLeftRight
      };
      return acc;
    }, {
      transformedLabelThemeTokens: {},
      transformedMainTextThemeTokens: {},
      transformedContainerThemeTokens: {}
    });
    const labelMediaQueryStyles = (0, _componentsBase.createMediaQueryStyles)(transformedLabelThemeTokens);
    const mainTestMediaQueryStyles = (0, _componentsBase.createMediaQueryStyles)(transformedMainTextThemeTokens);
    const containerMediaQueryStyles = (0, _componentsBase.createMediaQueryStyles)(transformedContainerThemeTokens);
    const {
      ids,
      styles
    } = _componentsBase.StyleSheet.create({
      label: {
        ...themeTokens,
        ...labelMediaQueryStyles
      },
      mainText: {
        ...themeTokens,
        ...mainTestMediaQueryStyles
      },
      container: {
        ...themeTokens,
        ...containerMediaQueryStyles
      }
    });
    labelStyles = getLabelTokens(styles.label);
    labelMediaIds = ids.label;
    mainTextStyles = getMainTextTokens(styles.mainText);
    mainTextMediaIds = ids.mainText;
    containerStyles = styles.container;
    containerMediaIds = ids.container;
  } else {
    labelStyles = labelTokens;
    mainTextStyles = mainTextTokens;
    containerStyles = themeTokens;
  }
  const divider = noDivider === true ?
  /*#__PURE__*/
  // StackView-based subcontainer adds a 1-step space on the each side of the divider
  (0, _jsxRuntime.jsx)(_componentsBase.Spacer, {
    direction: "row",
    space: (feature || large) && viewport !== _systemConstants.viewports.xs ? 4 : 1
  }) : /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Typography, {
    tokens: mainTextTokens,
    children: ":"
  });
  const segmentProps = {
    copy,
    labelTokens: labelStyles,
    numberTokens: mainTextStyles,
    labelMediaIds,
    mainTextMediaIds,
    segmentFontSize,
    variant
  };
  return (
    /*#__PURE__*/
    // Making it focusable for accessibility purposes
    (0, _jsxRuntime.jsx)(Container, {
      ref: ref,
      variant: variant,
      ...selectProps(rest),
      tabIndex: 0,
      themeTokens: containerStyles,
      gradient: semanticGradient,
      media: containerMediaIds,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.StackView, {
        direction: "row",
        space: 1,
        children: units.map((unit, index) => {
          let number;
          let labelKey;
          switch (unit) {
            case DAYS:
              number = days;
              labelKey = 'day';
              break;
            case HOURS:
              number = hours;
              labelKey = 'hour';
              break;
            case MINUTES:
              number = minutes;
              labelKey = 'minute';
              break;
            case SECONDS:
              number = seconds;
              labelKey = 'second';
              break;
            default:
              return null;
          }
          return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_react.default.Fragment, {
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Segment.default, {
              labelKey: labelKey,
              number: number,
              ...(unit === DAYS && {
                segmentWidth: String(number).length
              }),
              ...segmentProps,
              ...(0, _componentsBase.applyTextStyles)(themeTokens)
            }), index < units.length - 1 && divider]
          }, unit);
        })
      })
    })
  );
});
Countdown.displayName = 'Countdown';
Countdown.propTypes = {
  ...selectedSystemPropTypes,
  tokens: (0, _componentsBase.getTokensPropType)('Countdown'),
  /**
   * Copy language identifier (`'en'` or `'fr'`) or a dictionary instance (an object with
   * the following keys: days, day, hours, hour, minutes, minute, seconds, second)
   */
  copy: _propTypes.default.oneOfType([_propTypes.default.oneOf(['en', 'fr']), _types.dictionaryContentShape]),
  /**
   * An instance of JavaScript `Date` object or a string parseable via `Date.parse()`
   * representing a point in the future to count down to.
   */
  targetTime: _propTypes.default.instanceOf(Date),
  variant: _types.countdownVariantPropType,
  /**
   * An array of strings representing the units to display in the countdown.
   * Possible values: `'days'`, `'hours'`, `'minutes'`, `'seconds'`
   */
  units: _propTypes.default.arrayOf(_propTypes.default.oneOf([DAYS, HOURS, MINUTES, SECONDS]))
};
var _default = exports.default = Countdown;